/**
 * Upload helpers for the admin SPA: File/Blob → base64 (the API takes JSON-encoded bytes), EXIF
 * capture-date reading, iPhone HEIC → JPEG conversion, and the ≤2048 px JPEG RENDITION made for
 * move-out photos and receipt images.
 *
 * Originals are ALWAYS kept: the SPA uploads the untouched original (EXIF intact — capture metadata
 * can matter as evidence) AND, when it is actually smaller, a rendition; the server links them
 * (`documents.derived_from_document_id`) and the statement PDF embeds the rendition.
 *
 * HEIC decoding (the PDF embedder can only place JPEG/PNG):
 *   1. Native — Safari (macOS/iOS) decodes HEIC itself via createImageBitmap. No extra code.
 *      (iOS Safari usually transcodes to JPEG on selection anyway when `accept` is `image/*`.)
 *   2. Fallback — lazy-load `heic2any` (a ~1.3 MB libheif chunk, never in the main bundle). NOTE:
 *      heic2any runs libheif in a blob: Worker and uses `new Function`, which the production CSP
 *      (`script-src 'self'`, no 'unsafe-eval') blocks — on the deployed site this fallback fails and
 *      the user gets a clear message instead.
 */

/** Read a Blob as base64 WITHOUT the `data:...;base64,` prefix. */
export function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result;
      if (typeof result !== 'string') {
        reject(new Error('Could not read the file.'));
        return;
      }
      const comma = result.indexOf(',');
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.onerror = () => reject(reader.error ?? new Error('Could not read the file.'));
    reader.readAsDataURL(blob);
  });
}

/** Is this an iPhone HEIC/HEIF image (by MIME type or extension — some browsers leave type blank)? */
export function isHeic(file: File): boolean {
  return /^image\/hei[cf]/i.test(file.type) || /\.hei[cf]$/i.test(file.name);
}

/** Is this an image we should decode/render (vs. a PDF or other document passed through as-is)? */
export function isImageFile(file: File): boolean {
  return isHeic(file) || /^image\//i.test(file.type) || /\.(jpe?g|png|gif|webp)$/i.test(file.name);
}

/* ------------------------------------------------------------------ *
 * EXIF DateTimeOriginal — a tiny dependency-free reader.              *
 * ------------------------------------------------------------------ */

/** How much of the file to scan for the EXIF block (it lives near the start in JPEG and HEIC). */
const EXIF_SCAN_BYTES = 512 * 1024;

/**
 * Parse EXIF `DateTimeOriginal` (tag 0x9003, "YYYY:MM:DD HH:MM:SS") — plus `OffsetTimeOriginal`
 * (0x9011, "+07:00") when present — out of raw bytes, returning an ISO instant or null. Works for
 * JPEG (APP1) and HEIC (the `Exif` item) alike by locating the `Exif\0\0` marker + TIFF header,
 * then walking IFD0 → the Exif sub-IFD. Without an offset tag the camera's wall-clock time is
 * interpreted in the browser's local zone. Never throws.
 */
export function parseExifDateTimeOriginal(bytes: Uint8Array): string | null {
  try {
    // Find "Exif\0\0" followed by a TIFF header ("II" or "MM").
    let tiff = -1;
    for (let i = 0; i + 10 < bytes.length; i++) {
      if (
        bytes[i] === 0x45 &&
        bytes[i + 1] === 0x78 &&
        bytes[i + 2] === 0x69 &&
        bytes[i + 3] === 0x66 &&
        bytes[i + 4] === 0 &&
        bytes[i + 5] === 0
      ) {
        const b0 = bytes[i + 6];
        const b1 = bytes[i + 7];
        if ((b0 === 0x49 && b1 === 0x49) || (b0 === 0x4d && b1 === 0x4d)) {
          tiff = i + 6;
          break;
        }
      }
    }
    if (tiff < 0) return null;
    const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
    const le = bytes[tiff] === 0x49;
    const u16 = (o: number) => view.getUint16(tiff + o, le);
    const u32 = (o: number) => view.getUint32(tiff + o, le);
    const ascii = (o: number, n: number) => {
      let s = '';
      for (let k = 0; k < n; k++) {
        const ch = bytes[tiff + o + k];
        if (ch === undefined || ch === 0) break;
        s += String.fromCharCode(ch);
      }
      return s;
    };
    /** An ASCII tag's value (inline when ≤ 4 bytes, else at the offset). */
    const asciiValue = (entry: number) => {
      const count = u32(entry + 4);
      return count <= 4 ? ascii(entry + 8, count) : ascii(u32(entry + 8), count);
    };
    const findTag = (ifd: number, tag: number): number | null => {
      const n = u16(ifd);
      for (let k = 0; k < n; k++) {
        const entry = ifd + 2 + k * 12;
        if (u16(entry) === tag) return entry;
      }
      return null;
    };
    const exifPtr = findTag(u32(4), 0x8769);
    if (exifPtr == null) return null;
    const exifIfd = u32(exifPtr + 8);
    const dtEntry = findTag(exifIfd, 0x9003);
    if (dtEntry == null) return null;
    const m = /^(\d{4}):(\d{2}):(\d{2}) (\d{2}):(\d{2}):(\d{2})/.exec(asciiValue(dtEntry));
    if (!m) return null;
    const offEntry = findTag(exifIfd, 0x9011);
    const offset = offEntry != null ? asciiValue(offEntry) : '';
    const [, y, mo, d, h, mi, s] = m;
    if (/^[+-]\d{2}:\d{2}$/.test(offset)) {
      const t = Date.parse(`${y}-${mo}-${d}T${h}:${mi}:${s}${offset}`);
      return Number.isNaN(t) ? null : new Date(t).toISOString();
    }
    const local = new Date(Number(y), Number(mo) - 1, Number(d), Number(h), Number(mi), Number(s));
    return Number.isNaN(local.getTime()) ? null : local.toISOString();
  } catch {
    return null;
  }
}

/** EXIF capture time of a file (scans the first 512 KB), or null. */
export async function readExifCapturedAt(file: Blob): Promise<string | null> {
  try {
    const buf = new Uint8Array(await file.slice(0, EXIF_SCAN_BYTES).arrayBuffer());
    return parseExifDateTimeOriginal(buf);
  } catch {
    return null;
  }
}

/* ------------------------------------------------------------------ *
 * Decoding + renditions.                                              *
 * ------------------------------------------------------------------ */

/** Rendition geometry/quality (operator spec: long edge ≤ 2048 px, JPEG ~0.82). */
export const RENDITION_MAX_EDGE = 2048;
export const RENDITION_QUALITY = 0.82;

/** Re-encode a decodable image as JPEG, scaled so the long edge ≤ `maxEdge`. Null if it can't. */
async function encodeJpeg(source: Blob, maxEdge: number, quality: number): Promise<Blob | null> {
  if (typeof createImageBitmap !== 'function') return null;
  let bmp: ImageBitmap;
  try {
    bmp = await createImageBitmap(source);
  } catch {
    return null; // not decodable in this browser (e.g. HEIC outside Safari)
  }
  try {
    const scale = Math.min(1, maxEdge / Math.max(bmp.width, bmp.height));
    const w = Math.max(1, Math.round(bmp.width * scale));
    const h = Math.max(1, Math.round(bmp.height * scale));
    if (typeof OffscreenCanvas !== 'undefined') {
      const canvas = new OffscreenCanvas(w, h);
      const ctx = canvas.getContext('2d');
      if (!ctx) return null;
      ctx.drawImage(bmp, 0, 0, w, h);
      return await canvas.convertToBlob({ type: 'image/jpeg', quality });
    }
    if (typeof document === 'undefined') return null;
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;
    ctx.drawImage(bmp, 0, 0, w, h);
    return await new Promise<Blob | null>((resolve) =>
      canvas.toBlob((b) => resolve(b), 'image/jpeg', quality),
    );
  } catch {
    return null;
  } finally {
    bmp.close?.();
  }
}

/** HEIC → a JPEG blob: native decode first, then the lazy heic2any fallback. */
async function heicToJpeg(file: File): Promise<Blob> {
  const native = await encodeJpeg(file, RENDITION_MAX_EDGE, RENDITION_QUALITY);
  if (native) return native;
  try {
    const { default: heic2any } = await import('heic2any');
    const out = await heic2any({ blob: file, toType: 'image/jpeg', quality: RENDITION_QUALITY });
    const blob = Array.isArray(out) ? out[0] : out;
    if (!blob) throw new Error('empty conversion');
    return blob;
  } catch {
    throw new Error(
      `Couldn't convert ${file.name} from HEIC to JPEG in this browser. Upload it from Safari (which converts automatically), export it as JPEG, or set iPhone Settings → Camera → Formats → Most Compatible.`,
    );
  }
}

export interface Uploadable {
  blob: Blob;
  filename: string;
  contentType: string;
}

export interface PreparedUpload {
  /** What the report attaches (the rendition when one was made, else the original). */
  primary: Uploadable;
  /** The untouched original, sent alongside ONLY when `primary` is a rendition. */
  original: Uploadable | null;
  /** EXIF DateTimeOriginal (ISO) → else the file's last-modified time. */
  capturedAt: string;
}

function renditionName(name: string): string {
  return `${name.replace(/\.[^.]+$/, '')}.jpg`;
}

/**
 * Prepare a selected file for a move-out upload.
 *  - Non-images (PDF receipts …): passed through as-is, no rendition.
 *  - HEIC/HEIF: the original HEIC is kept + a JPEG rendition (≤2048 px) is ALWAYS made — the PDF
 *    can't embed HEIC — via native decode, else heic2any (then downscaled when decodable).
 *  - Other images: a ≤2048 px JPEG rendition, used only if it is SMALLER than the original;
 *    otherwise the original alone is uploaded.
 * EXIF DateTimeOriginal is read from the ORIGINAL (renditions are canvas re-encodes, which strip
 * EXIF — fine, the original retains it).
 */
export async function prepareMoveOutUpload(file: File): Promise<PreparedUpload> {
  const original: Uploadable = {
    blob: file,
    filename: file.name,
    contentType: file.type || (isHeic(file) ? 'image/heic' : 'application/octet-stream'),
  };
  const capturedAt =
    (await readExifCapturedAt(file)) ?? new Date(file.lastModified || Date.now()).toISOString();
  if (!isImageFile(file)) return { primary: original, original: null, capturedAt };

  if (isHeic(file)) {
    let jpeg = await heicToJpeg(file);
    // heic2any output is full-size; bring it within the rendition bound too.
    const resized = await encodeJpeg(jpeg, RENDITION_MAX_EDGE, RENDITION_QUALITY);
    if (resized && resized.size < jpeg.size) jpeg = resized;
    return {
      primary: { blob: jpeg, filename: renditionName(file.name), contentType: 'image/jpeg' },
      original,
      capturedAt,
    };
  }

  const rendition = await encodeJpeg(file, RENDITION_MAX_EDGE, RENDITION_QUALITY);
  if (!rendition || rendition.size >= file.size) {
    return { primary: original, original: null, capturedAt };
  }
  return {
    primary: { blob: rendition, filename: renditionName(file.name), contentType: 'image/jpeg' },
    original,
    capturedAt,
  };
}
